import { useEffect, useState } from "react";
import { Plus, Bell, Trash2, PoundSterling, CreditCard, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, Panel, Pill, Empty } from "@/components/kit";
import { api, errMsg, gbp, fmtDay } from "@/lib/api";
import { FormDialog, SectionHead, Toggle } from "./common";

const BLANK = { title: "", amount: "", due_date: "", all_players: true, member_ids: [] };

const ChargeRow = ({ c, onToggle }) => (
  <div className="flex items-center justify-between gap-3 py-2" data-testid={`charge-row-${c.id}`}>
    <span className="text-sm text-slate-200">{c.member_name}</span>
    <div className="flex items-center gap-2">
      {c.status === "paid" ? (
        <Pill tone="green">{c.paid_via === "stripe" ? <CreditCard className="h-3 w-3" /> : <Check className="h-3 w-3" />}paid {c.paid_via === "stripe" ? "by card" : "cash/bank"}</Pill>
      ) : <Pill tone="amber">owed</Pill>}
      {c.paid_via !== "stripe" && (
        <button className="text-xs font-semibold text-[#CCFF00] hover:underline" onClick={() => onToggle(c)} data-testid={`toggle-charge-${c.id}`}>
          {c.status === "paid" ? "Undo" : "Mark paid"}
        </button>
      )}
    </div>
  </div>
);

const FeeCard = ({ fee, onToggle, onRemind, onDelete }) => {
  const pct = fee.charges.length ? Math.round((fee.paid_count / fee.charges.length) * 100) : 0;
  return (
    <Panel data-testid={`fee-card-${fee.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-display text-2xl font-bold uppercase text-white">{fee.title}</p>
          <p className="font-mono text-xs text-slate-400">{gbp(fee.amount)} each{fee.due_date && ` · due ${fmtDay(fee.due_date)}`}</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="rounded-full border-white/15 bg-transparent" onClick={() => onRemind(fee)} data-testid={`remind-fee-${fee.id}`}><Bell className="mr-1 h-3.5 w-3.5" /> Remind unpaid</Button>
          <Button size="icon" variant="ghost" className="text-rose-400" onClick={() => onDelete(fee)} data-testid={`delete-fee-${fee.id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="mt-5 flex items-end justify-between">
        <p className="font-display text-4xl font-black text-white" data-testid={`fee-collected-${fee.id}`}>{gbp(fee.collected)}</p>
        <p className="font-mono text-xs text-slate-400">{fee.paid_count}/{fee.charges.length} paid</p>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/5"><div className="h-full bg-[#CCFF00] transition-all duration-700" style={{ width: `${pct}%` }} /></div>
      <div className="mt-4 divide-y divide-white/5">{fee.charges.map((c) => <ChargeRow key={c.id} c={c} onToggle={onToggle} />)}</div>
    </Panel>
  );
};

export default function Fees({ teamId }) {
  const [fees, setFees] = useState([]);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(BLANK);

  const load = () => api.get(`/teams/${teamId}/fees`).then((r) => setFees(r.data));
  useEffect(() => {
    load();
    api.get(`/teams/${teamId}/members`).then((r) => setMembers(r.data));
  }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    try {
      const { data } = await api.post(`/teams/${teamId}/fees`, { ...f, amount: Number(f.amount) });
      toast.success(`Fee added for ${data.charge_count} members`);
      setOpen(false);
      setF(BLANK);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (c) => {
    try {
      await api.patch(`/teams/${teamId}/charges/${c.id}`, { status: c.status === "paid" ? "unpaid" : "paid" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remind = async (fee) => {
    const { data } = await api.post(`/teams/${teamId}/fees/${fee.id}/remind`);
    toast.success(`Reminder sent to ${data.reminded} members`);
  };
  const del = async (fee) => {
    if (!window.confirm(`Delete ${fee.title} and all its charges?`)) return;
    await api.delete(`/teams/${teamId}/fees/${fee.id}`);
    load();
  };
  const pick = (id) => setF({ ...f, member_ids: f.member_ids.includes(id) ? f.member_ids.filter((x) => x !== id) : [...f.member_ids, id] });

  return (
    <div className="space-y-6">
      <SectionHead title="Fees" text="Subs and match fees in £. Players pay by card in their app, or mark cash payments here.">
        <Button className="rounded-full font-bold" onClick={() => setOpen(true)} data-testid="add-fee-btn"><Plus className="mr-1 h-4 w-4" /> New fee</Button>
      </SectionHead>
      {!fees.length && <Empty icon={PoundSterling} title="No fees yet" text="Create season subs or a match fee." />}
      <div className="grid gap-5 xl:grid-cols-2">{fees.map((fee) => <FeeCard key={fee.id} fee={fee} onToggle={toggle} onRemind={remind} onDelete={del} />)}</div>
      <FormDialog open={open} onOpenChange={setOpen} title="New fee" onSubmit={create} submitLabel="Create fee" testid="fee-form">
        <Field label="Title"><Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Match fee vs Northside" data-testid="fee-title-input" /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Amount (£)"><Input type="number" step="0.01" min="0.5" required value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="fee-amount-input" /></Field>
          <Field label="Due date"><Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="fee-due-input" /></Field>
        </div>
        <Toggle checked={f.all_players} onChange={(v) => setF({ ...f, all_players: v })} label="Charge all players" testid="fee-all-players-toggle" />
        {!f.all_players && (
          <div className="max-h-48 space-y-2 overflow-y-auto rounded-md border border-white/10 p-3">
            {members.map((m) => <Toggle key={m.id} checked={f.member_ids.includes(m.id)} onChange={() => pick(m.id)} label={`${m.name} (${m.role})`} testid={`fee-member-${m.id}`} />)}
          </div>
        )}
      </FormDialog>
    </div>
  );
}
